import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../firebase/firestore';
import { uploadFile, deleteFile } from '../../firebase/storage';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import './AdminPages.css';

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    position: '',
    testimonial: '',
    logo: '',
    active: true,
    order: 1
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { db } = await import('../../firebase/config');
      
      if (!db) {
        setLoading(false);
        return;
      }

      const data = await getCollection('partners');
      setPartners(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        alert('Image is too large. Please use an image under 500KB or enter an image URL instead.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...formData };
      
      // If a new image file was uploaded, try to upload it to Firebase Storage
      if (imageFile) {
        try {
          const timestamp = Date.now();
          const fileName = `partners/${timestamp}_${imageFile.name}`;
          const imageUrl = await uploadFile(imageFile, fileName);
          dataToSave.logo = imageUrl;
          
          // If editing and there was an old image, delete it
          if (editingPartner && editingPartner.logo && editingPartner.logo.includes('firebase')) {
            try {
              const oldImagePath = editingPartner.logo.split('/o/')[1]?.split('?')[0];
              if (oldImagePath) {
                await deleteFile(decodeURIComponent(oldImagePath));
              }
            } catch (err) {
              console.log('Could not delete old image:', err);
            }
          }
        } catch (uploadError) {
          console.error('Storage upload failed:', uploadError);
          alert('Firebase Storage is not configured. Please enter an image URL instead or configure Firebase Storage CORS settings.');
          return;
        }
      }

      if (editingPartner) {
        await updateDocument('partners', editingPartner.id, dataToSave);
      } else {
        await addDocument('partners', dataToSave);
      }
      fetchPartners();
      resetForm();
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('Error saving partner: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        const partner = partners.find(p => p.id === id);
        
        // Delete the image from storage if it exists
        if (partner && partner.logo && partner.logo.includes('firebase')) {
          try {
            const imagePath = partner.logo.split('/o/')[1]?.split('?')[0];
            if (imagePath) {
              await deleteFile(decodeURIComponent(imagePath));
            }
          } catch (err) {
            console.log('Could not delete image:', err);
          }
        }
        
        await deleteDocument('partners', id);
        fetchPartners();
      } catch (error) {
        console.error('Error deleting partner:', error);
      }
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      company: partner.company,
      position: partner.position,
      testimonial: partner.testimonial,
      logo: partner.logo || '',
      active: partner.active,
      order: partner.order
    });
    setImagePreview(partner.logo || '');
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      company: '', 
      position: '', 
      testimonial: '', 
      logo: '', 
      active: true, 
      order: 1 
    });
    setEditingPartner(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview('');
  };

  if (loading) return <Loader />;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Partners</h1>
          <p>Manage trusted companies</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Partner'}
        </Button>
      </div>

      {showForm && (
        <motion.div
          className="admin-form-card"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h3>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                minLength={2}
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label>Company *</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
                minLength={2}
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label>Position *</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                required
                minLength={2}
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label>Testimonial *</label>
              <textarea
                value={formData.testimonial}
                onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
                rows="5"
                required
                minLength={10}
                maxLength={500}
                placeholder="Partner testimonial message"
              />
            </div>
            <div className="form-group">
              <label>Logo URL (Optional)</label>
              <input
                type="url"
                placeholder="https://example.com/logo.jpg"
                value={formData.logo}
                onChange={(e) => {
                  setFormData({ ...formData, logo: e.target.value });
                  setImagePreview(e.target.value);
                }}
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Enter an image URL (recommended) or upload a file below (max 500KB)
              </small>
            </div>
            <div className="form-group">
              <label>Or Upload Logo (max 500KB)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', marginTop: '1rem', borderRadius: '8px' }} />
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="form-actions">
              <Button type="submit">{editingPartner ? 'Update' : 'Create'} Partner</Button>
              <Button variant="secondary" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="admin-page-content">
        {partners.length === 0 ? (
          <div className="empty-state-card">
            <p>🤝 No partners yet. Click "Add Partner" to create one.</p>
          </div>
        ) : (
          <div className="admin-grid">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.id}
                className="admin-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {partner.logo && (
                  <div className="portfolio-card-image">
                    <img src={partner.logo} alt={partner.name} />
                  </div>
                )}
                <div className="portfolio-card-content">
                  <div className="admin-card-header">
                    <h3>{partner.name}</h3>
                    <span className={`status-badge ${partner.active ? 'active' : 'inactive'}`}>
                      {partner.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="portfolio-meta">
                    <span className="client-name">{partner.position} at {partner.company}</span>
                  </div>
                  <p>{partner.testimonial.substring(0, 100)}{partner.testimonial.length > 100 ? '...' : ''}</p>
                  <div className="admin-card-footer">
                    <span>Order: {partner.order}</span>
                    <div className="admin-card-actions">
                      <button onClick={() => handleEdit(partner)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(partner.id)} className="btn-delete">Delete</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartners;
